// Meta Conversion API client-side utility
// Sends events to serverless function which forwards to Meta's Conversion API

import { getFbclid } from './facebookPixel';

// Get or generate client user data for event tracking
const getClientUserData = () => {
  // Get fbclid (Facebook Click ID) from URL or storage
  const fbclid = getFbclid();
  
  // Get or generate client IP (will be captured server-side)
  const userAgent = navigator?.userAgent || '';
  
  // Get fbp cookie (Facebook browser ID)
  const fbp = document?.cookie
    ?.split('; ')
    ?.find(row => row?.startsWith('_fbp='))
    ?.split('=')?.[1] || null;
  
  // Get fbc cookie (Facebook click ID from cookie)
  const fbc = document?.cookie
    ?.split('; ')
    ?.find(row => row?.startsWith('_fbc='))
    ?.split('=')?.[1] || null;
  
  return {
    fbp,
    fbc: fbc || (fbclid ? `fb.1.${Date.now()}.${fbclid}` : null),
    user_agent: userAgent,
    client_ip_address: null, // Will be captured server-side
    client_user_agent: userAgent
  };
};

// Generate event ID to prevent duplicate events
const generateEventId = () => {
  return `${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
};

// Send event to serverless function
const sendConversionEvent = async (eventName, eventData = {}, customData = {}) => {
  const apiEndpoint = import.meta.env?.VITE_CONVERSION_API_ENDPOINT;
  
  if (!apiEndpoint || apiEndpoint === 'your-serverless-endpoint-here') {
    console.warn('Conversion API endpoint not configured. Event not sent:', eventName);
    return { success: false, message: 'Endpoint not configured' };
  }
  
  const eventId = generateEventId();
  const userData = getClientUserData();
  
  const payload = {
    event_name: eventName,
    event_time: Math.floor(Date.now() / 1000),
    event_id: eventId,
    event_source_url: window?.location?.href,
    action_source: 'website',
    user_data: {
      ...userData,
      ...eventData?.user_data
    },
    custom_data: customData
  };
  
  try {
    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response?.ok) {
      throw new Error(`HTTP error! status: ${response?.status}`);
    }
    
    const result = await response?.json();
    console.log('Conversion API event sent:', eventName, result);
    return { success: true, result };
  } catch (error) {
    console.error('Error sending Conversion API event:', error);
    return { success: false, error: error?.message };
  }
};

// Standard event tracking functions
export const trackPageViewAPI = async () => {
  return await sendConversionEvent('PageView', {}, {
    page_title: document?.title,
    page_url: window?.location?.href
  });
};

export const trackViewContentAPI = async (contentName, contentCategory) => {
  return await sendConversionEvent('ViewContent', {}, {
    content_name: contentName,
    content_category: contentCategory
  });
};

// CONVERSION TRACKING DISABLED - Handled by GHL server-side
// Keeping these functions commented to prevent double counting in Facebook Ads

// export const trackLeadAPI = async (email, contentName) => {
//   // Hash email client-side for privacy
//   const hashedEmail = email ? await hashEmail(email) : null;
//   
//   return await sendConversionEvent(
//     'Lead',
//     {
//       user_data: {
//         em: hashedEmail
//       }
//     },
//     {
//       content_name: contentName
//     }
//   );
// };

// export const trackCompleteRegistrationAPI = async (email, contentName) => {
//   const hashedEmail = email ? await hashEmail(email) : null;
//   
//   return await sendConversionEvent(
//     'CompleteRegistration',
//     {
//       user_data: {
//         em: hashedEmail
//       }
//     },
//     {
//       content_name: contentName,
//       status: 'completed'
//     }
//   );
// };

// export const trackInitiateCheckoutAPI = async (contentName, contentCategory) => {
//   return await sendConversionEvent('InitiateCheckout', {}, {
//     content_name: contentName,
//     content_category: contentCategory
//   });
// };

export const trackCustomEventAPI = async (eventName, customData = {}) => {
  return await sendConversionEvent(eventName, {}, customData);
};

// Helper function to hash email using SHA-256
const hashEmail = async (email) => {
  if (!email) return null;
  
  const normalized = email?.toLowerCase()?.trim();
  const encoder = new TextEncoder();
  const data = encoder?.encode(normalized);
  const hashBuffer = await crypto?.subtle?.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray?.map(b => b?.toString(16)?.padStart(2, '0'))?.join('');
  return hashHex;
};

// Dual tracking: Send to both Pixel and Conversion API
export const trackDualEvent = async (eventName, pixelParams = {}, apiCustomData = {}) => {
  // Track with Facebook Pixel (client-side)
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, pixelParams);
  }
  
  // Track with Conversion API (server-side)
  return await sendConversionEvent(eventName, {}, apiCustomData);
};

export default {
  trackPageViewAPI,
  trackViewContentAPI,
  trackCustomEventAPI,
  trackDualEvent
};